import { session } from '@/lib/session'

/**
 * The one place that talks to the Saadhan Charge API.
 *
 * - Every call goes to /api/* on this origin; the dev server and the
 *   Cloudflare Worker forward it to the API (no CORS).
 * - The API wraps responses as {success, message, data}; callers get `data`.
 * - A 401 refreshes the access token once (concurrent calls wait for the same
 *   refresh) and retries; if that fails the session ends.
 */

export const API_BASE = '/api'

export class ApiError extends Error {
  readonly status: number
  readonly detail?: string
  constructor(status: number, message: string, detail?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
  /** The endpoint doesn't exist on the server yet (a feature waiting for the backend). */
  get isMissing() {
    return this.status === 404 || this.status === 405 || this.status === 501
  }
}

type Query = Record<string, string | number | boolean | null | undefined | (string | number)[]>

export interface RequestOptions {
  query?: Query
  body?: unknown
  form?: FormData
  /** Return the raw Response (file downloads). */
  raw?: boolean
  signal?: AbortSignal
}

function buildUrl(path: string, query?: Query) {
  const url = new URL(API_BASE + path, window.location.origin)
  const params = query ?? {}
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || String(v) === '') continue
    if (Array.isArray(v)) v.forEach((item) => url.searchParams.append(k, String(item)))
    else url.searchParams.set(k, String(v))
  }
  return url.pathname + url.search
}

let refreshing: { revision: number; promise: Promise<string | null> } | null = null

async function refreshAccessToken(): Promise<string | null> {
  const revision = session.revision()
  const refreshToken = session.get()?.refreshToken
  if (!refreshToken) return null
  if (refreshing?.revision === revision) return refreshing.promise
  const promise = (async (): Promise<string | null> => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })
      if (!res.ok) return null
      const json = await res.json()
      const token: string | undefined = json?.data?.access_token
      if (!token || revision !== session.revision()) return null
      session.updateAccessToken(token)
      return token
    } catch {
      return null
    }
  })().finally(() => {
    // Cleared before any waiting caller resumes, so a later 401 starts afresh.
    if (refreshing?.revision === revision) refreshing = null
  })
  refreshing = { revision, promise }
  return promise
}

async function parseError(res: Response): Promise<ApiError> {
  let message = res.statusText || `Request failed (${res.status})`
  let detail: string | undefined
  try {
    const text = await res.text()
    try {
      const json = JSON.parse(text)
      message = json.message || json.detail?.toString?.() || message
      detail = typeof json.error === 'string' ? json.error : Array.isArray(json.detail) ? json.detail.map((d: { msg?: string }) => d.msg).join(', ') : undefined
      if (json.message && detail && detail !== json.message) message = `${json.message}: ${detail}`
    } catch {
      if (text && text.length < 200) message = text
    }
  } catch {
    /* body unreadable */
  }
  return new ApiError(res.status, message, detail)
}

export async function request<T = unknown>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const revision = session.revision()
  const personal = !path.startsWith('/public/')
  const checkIdentity = () => {
    if (personal && revision !== session.revision()) throw new ApiError(401, 'The account changed. Please try again.')
  }
  const send = (token?: string) => {
    const headers: Record<string, string> = { accept: 'application/json' }
    if (token) headers.authorization = `Bearer ${token}`
    let body: BodyInit | undefined
    if (options.form) body = options.form
    else if (options.body !== undefined) {
      headers['content-type'] = 'application/json'
      body = JSON.stringify(options.body)
    }
    return fetch(buildUrl(path, options.query), { method, headers, body, signal: options.signal })
  }

  const used = session.get()?.accessToken
  let res = await send(used)
  checkIdentity()
  if (res.status === 401 && used && session.get()) {
    if (revision !== session.revision()) throw new ApiError(401, 'The account changed. Please try again.')
    // Another request may have refreshed while this one was in flight.
    const latest = session.get()?.accessToken
    const token = latest && latest !== used ? latest : await refreshAccessToken()
    if (revision !== session.revision()) throw new ApiError(401, 'The account changed. Please try again.')
    checkIdentity()
    if (!token) {
      session.set(null)
      throw new ApiError(401, 'Your session has ended. Please sign in again.')
    }
    res = await send(token)
    if (revision !== session.revision()) throw new ApiError(401, 'The account changed. Please try again.')
    checkIdentity()
    if (res.status === 401) {
      session.set(null)
      throw new ApiError(401, 'Your session has ended. Please sign in again.')
    }
  }
  if (!res.ok) {
    const error = await parseError(res)
    checkIdentity()
    throw error
  }
  if (options.raw) return res as unknown as T
  if (res.status === 204) return undefined as T
  const text = await res.text()
  checkIdentity()
  if (!text) return undefined as T
  let json: unknown
  try {
    json = JSON.parse(text)
  } catch {
    return text as unknown as T
  }
  if (json && typeof json === 'object' && 'success' in json) {
    const env = json as { success: boolean; message?: string; data?: unknown; error?: string }
    if (env.success === false) throw new ApiError(res.status, env.message || 'Request failed', env.error)
    return (env.data === undefined ? ({ message: env.message } as unknown) : env.data) as T
  }
  return json as T
}

export const api = {
  get: <T>(path: string, query?: Query, opts?: Omit<RequestOptions, 'query'>) => request<T>('GET', path, { ...opts, query }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, { ...opts, body }),
  del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
}

/** Files the API serves under /uploads (photos, carousel images). */
export function uploadUrl(path?: string | null) {
  if (!path) return undefined
  if (/^https?:/.test(path)) return path
  return `${API_BASE}/uploads/${path.replace(/^\/?(uploads\/)?/, '')}`
}

/** Saves a file response (session exports). */
export async function download(path: string, query: Query, fallbackName: string) {
  const res = await request<Response>('GET', path, { query, raw: true })
  const blob = await res.blob()
  const disposition = res.headers.get('content-disposition') ?? ''
  const name = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? fallbackName
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
